import Menu from "@mui/material/Menu";
import type React from "react";
import { createContext, useContext, useState } from "react";

import type { OpenTask } from "@storyboard/dispatch-view/task";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import {
  type AcknowledgementMenuAction,
  type DispatchMenuAction,
  type DispatchMenuEntry,
  dispatchMenuEntries,
} from "@/hooks/dispatch/dispatchMenu";
import type { ThreadTaskCard } from "@/hooks/dispatch/dispatchThread";
import { type TaskMenuAction, taskMenuActionsFor } from "@/hooks/dispatch/taskMenu";
import { type ItemTarget, openTaskOfTarget, type TaskFold } from "@/hooks/dispatch/tasks";
import { keepsBrowserMenu } from "@/ui/textEntry";
import { zinc } from "@/ui/tokens";
import { dispatchMenuItems } from "../dispatchMenuItems";
import { DispatchTaskContext } from "../taskChip";

/**
 * What History's right-click menus write through: the page's acknowledgement writer, so Ctrl or Cmd with Z takes each
 * write back as it does the keys'.
 */
export type DispatchHistoryActions = {
  /** Handles a message or event, or takes its handling back. */
  onItemAcknowledgement: (target: ItemTarget, action: AcknowledgementMenuAction) => void;
  /** Closes an open task with the messages or events it is about, or reopens a done one. */
  onTaskAcknowledgement: (taskId: string, action: AcknowledgementMenuAction) => void;
  /** Handles the open channel, as acknowledging the driver's channel does, or takes its watermark back. */
  onChannelAcknowledgement: (action: AcknowledgementMenuAction) => void;
  /** Whether anything waits on the open channel, which decides whether the background offers to handle it. */
  channelWaiting: boolean;
};

/** History's acknowledgement writes; null outside the Dispatch page, and the menus then offer no acknowledgement. */
export const DispatchHistoryContext = createContext<DispatchHistoryActions | null>(null);

/**
 * The task entries a right-click on a thread entry offers: make a task on the message or event, or on the driver the
 * panel is open on, then move the one already on the entry.
 *
 * While the workspace keeps tasks about a message or event off, a new task can name only the driver; a task the entry
 * already carries can still be moved.
 *
 * @param target - The message or event, or null for an event of a subject no task names.
 * @param fold - The workspace's open tasks.
 * @param hasDriver - Whether the History panel is open on a driver, whom a task can name instead of the entry.
 * @param itemTasks - Whether the workspace lets a new task be about a message or event.
 */
export function threadTaskActions(
  target: ItemTarget | null,
  fold: TaskFold,
  hasDriver: boolean,
  itemTasks: boolean,
): readonly TaskMenuAction[] {
  const nameable = (itemTasks && target !== null) || hasDriver;
  return taskMenuActionsFor(nameable, target ? openTaskOfTarget(target, fold) : null);
}

/**
 * The entries a right-click on a message or event offers: "Acknowledge", or "Unacknowledge" once it is handled, then
 * its task entries.
 *
 * @param acknowledged - Whether the message or event is handled; null where there is nothing to acknowledge, as on a
 *   dispatcher's own message or an event no log takes.
 * @param tasks - The entry's task entries, as {@link threadTaskActions} lists them; empty while tasks are off.
 */
export function threadEntryMenu(acknowledged: boolean | null, tasks: readonly TaskMenuAction[]): DispatchMenuEntry[] {
  return dispatchMenuEntries(acknowledged === null ? null : { noun: "item", acknowledged }, tasks, null);
}

/**
 * The entries a right-click on a task card offers: "Acknowledge task" and "Move task…" while it is open, and
 * "Unacknowledge task", which reopens it, once it is done. Never a new task.
 *
 * @param state - Whether the card's task is open or done.
 * @param open - The open task as the fold holds it; an open card the fold does not hold offers nothing.
 */
export function taskCardMenu(state: ThreadTaskCard["state"], open: OpenTask | undefined): DispatchMenuEntry[] {
  if (state === "done") return dispatchMenuEntries({ noun: "task", acknowledged: true }, [], null);
  if (open === undefined) return [];
  return dispatchMenuEntries({ noun: "task", acknowledged: false }, taskMenuActionsFor(false, open), null);
}

/**
 * The entries a right-click on History off every entry offers: "Acknowledge channel" while anything waits on it, else
 * "Unacknowledge channel", then "Make a task…" about the driver the panel is open on.
 *
 * @param channelWaiting - Whether anything waits on the open channel.
 * @param canMakeTask - Whether tasks are on and the panel is open on a driver a task can name.
 */
export function threadBackgroundMenu(channelWaiting: boolean, canMakeTask: boolean): DispatchMenuEntry[] {
  return dispatchMenuEntries(
    { noun: "channel", acknowledged: !channelWaiting },
    taskMenuActionsFor(canMakeTask, null),
    null,
  );
}

/** Whether a menu action is the acknowledgement entry's, rather than a task entry's. */
function isAcknowledgement(action: DispatchMenuAction): action is AcknowledgementMenuAction {
  return action.kind === "acknowledge" || action.kind === "unacknowledge";
}

/** A right-click's handler and the menu it opens. */
type ContextMenu = {
  onContextMenu: (event: React.MouseEvent<HTMLElement>) => void;
  menu: React.ReactNode;
};

/**
 * A right-click menu over History: the handler to put on the element, and the menu, which runs the chosen entry
 * through `run`.
 *
 * The element owns its right-click: the browser's menu never opens over it, and the right-click never reaches the
 * element around it. With no entries, nothing opens. A right-click in a field to type in keeps the browser's menu.
 */
function useHistoryContextMenu(
  entries: readonly DispatchMenuEntry[],
  run: (action: DispatchMenuAction) => void,
): ContextMenu {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const onContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (keepsBrowserMenu(event.target)) return;
    event.preventDefault();
    event.stopPropagation();
    if (entries.length > 0) setPosition({ top: event.clientY, left: event.clientX });
  };
  const close = () => setPosition(null);
  const pick = (action: DispatchMenuAction) => {
    close();
    run(action);
  };
  const menu =
    entries.length === 0 ? null : (
      <Menu
        open={position !== null}
        onClose={close}
        anchorReference="anchorPosition"
        anchorPosition={position ?? undefined}
        slotProps={{
          paper: {
            "data-sb": TestIds.dispatch.threadMenu,
            sx: { bgcolor: zinc[800], color: "white", minWidth: 200 },
          } as React.ComponentProps<typeof Menu>["PaperProps"],
          list: { dense: true },
        }}
      >
        {dispatchMenuItems(entries, pick)}
      </Menu>
    );
  return { onContextMenu, menu };
}

/**
 * The right-click on one message or event: the handler to put on it, and the menu it opens, which offers the entry's
 * acknowledgement and task entries, as the table's row menu does, and runs the chosen one through the page.
 *
 * @param target - The message or event the entry is about, or null for an event of a subject no task names.
 * @param acknowledged - Whether the entry is handled; null where there is nothing to acknowledge.
 */
export function useThreadEntryMenu(target: ItemTarget | null, acknowledged: boolean | null): ContextMenu {
  const tasks = useContext(DispatchTaskContext);
  const history = useContext(DispatchHistoryContext);
  const taskActions = tasks
    ? threadTaskActions(target, tasks.fold, tasks.historyDriverId !== null, tasks.itemTasks)
    : [];
  const entries = threadEntryMenu(history && target ? acknowledged : null, taskActions);
  return useHistoryContextMenu(entries, (action) => {
    if (!isAcknowledgement(action)) {
      tasks?.onItemTaskAction(target, action);
      return;
    }
    if (target) history?.onItemAcknowledgement(target, action);
  });
}

/**
 * The right-click on one task card: "Acknowledge task" and "Move task…" while it is open, "Unacknowledge task" once it
 * is done.
 *
 * The card never hands its right-click to the message or event around it.
 *
 * @param card - The task the card draws.
 */
export function useTaskCardMenu(card: ThreadTaskCard): ContextMenu {
  const tasks = useContext(DispatchTaskContext);
  const history = useContext(DispatchHistoryContext);
  const entries = tasks && history ? taskCardMenu(card.state, tasks.fold.byTask.get(card.id)) : [];
  return useHistoryContextMenu(entries, (action) => {
    if (isAcknowledgement(action)) history?.onTaskAcknowledgement(card.id, action);
    else tasks?.onItemTaskAction(null, action);
  });
}

/**
 * The right-click on History off every entry, empty space included: "Acknowledge channel", or its undoing, and
 * "Make a task…" about the panel's driver.
 */
export function useThreadBackgroundMenu(): ContextMenu {
  const history = useContext(DispatchHistoryContext);
  const tasks = useContext(DispatchTaskContext);
  const entries = history
    ? threadBackgroundMenu(history.channelWaiting, (tasks?.historyDriverId ?? null) !== null)
    : [];
  return useHistoryContextMenu(entries, (action) => {
    if (isAcknowledgement(action)) history?.onChannelAcknowledgement(action);
    else tasks?.onItemTaskAction(null, action);
  });
}
